import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { XCircle, Globe, FileText, Activity, Shield, CheckCircle, Eye } from 'lucide-react';

export const FailedLoginsCard = ({ data, formatTimestamp }) => {
  if (!data?.recent?.length) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2"><XCircle className="w-5 h-5 text-red-500" /><span>Recent Failed Login Attempts</span></CardTitle>
        <CardDescription>Last 10 failed login attempts in the past 24 hours</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {data.recent.map((attempt, index) => (
            <div key={`sec-${index}`} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
              <div><p className="text-sm font-medium text-slate-900">User: {attempt.user_id || 'Unknown'}</p><p className="text-xs text-slate-500">{attempt.ip_address} {formatTimestamp(attempt.timestamp)}</p></div>
              <Badge variant="destructive">Failed</Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const GeoAnomaliesCard = ({ anomalies }) => {
  const [selected, setSelected] = useState(null);
  if (!anomalies?.length) return null;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2"><Globe className="w-5 h-5 text-amber-500" /><span>Geographic Anomalies</span></CardTitle>
          <CardDescription>Users with unusual login patterns from multiple locations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {anomalies.map((anomaly, index) => (
              <div key={`sec-${index}`} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                <div>
                  <p className="text-sm font-medium text-slate-900">User: {anomaly._id}</p>
                  <p className="text-xs text-slate-500">{anomaly.count} logins from {anomaly.ips.length} different IPs</p>
                </div>
                <Button
                  variant="outline" size="sm" onClick={() => setSelected(anomaly)}
                  data-testid={`geo-anomaly-view-${index}`}
                  className="bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200"
                >
                  <Eye className="w-4 h-4 mr-1" />View
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <GeoAnomalyDetailDialog anomaly={selected} onClose={() => setSelected(null)} />
    </>
  );
};

const GeoAnomalyDetailDialog = ({ anomaly, onClose }) => (
  <Dialog open={!!anomaly} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="geo-anomaly-dialog">
      <DialogHeader>
        <DialogTitle>Login pattern for {anomaly?._id}</DialogTitle>
        <DialogDescription>
          {anomaly?.count} logins from {anomaly?.ips?.length} distinct IPs in the last 24 hours.
        </DialogDescription>
      </DialogHeader>
      <div className="max-h-72 overflow-y-auto divide-y">
        {(anomaly?.ips || []).map((ip, i) => (
          <div key={i} className="py-2 text-sm flex items-center justify-between">
            <span className="font-mono text-slate-800">{ip}</span>
            <Badge variant="outline">IP #{i + 1}</Badge>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-2">
        Note: some of these may be legitimate (mobile → home → VPN in the same day). Cross-check with the user&apos;s timezone before taking action.
      </p>
    </DialogContent>
  </Dialog>
);

export const ExportActivityCard = ({ data }) => {
  if (!data?.count_24h) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2"><FileText className="w-5 h-5 text-blue-500" /><span>Export Activity (24h)</span></CardTitle>
        <CardDescription>{data.count_24h} export operations in the last 24 hours</CardDescription>
      </CardHeader>
      <CardContent>
        {data.high_activity_users?.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700 mb-2">High Activity Users:</p>
            {data.high_activity_users.map((user, index) => (
              <div key={`sec-${index}`} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm text-slate-900">User: {user._id}</p>
                <Badge className="bg-blue-100 text-blue-800">{user.count} exports</Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const RecentActionsCard = ({ data }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center space-x-2"><Activity className="w-5 h-5 text-slate-600" /><span>Recent Security Actions</span></CardTitle>
      <CardDescription>Summary of recent security-related activities</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        {data?.counts && Object.entries(data.counts).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([action, count]) => (
          <div key={action} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
            <span className="text-sm text-slate-700">{action.replace(/_/g, ' ')}</span>
            <Badge variant="outline">{count}</Badge>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const IPWhitelistCard = ({ data }) => {
  if (!data) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2"><Shield className="w-5 h-5 text-emerald-600" /><span>IP Whitelist</span></CardTitle>
        <CardDescription>Admin panel access control</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-2">
          {data.enabled ? (
            <><CheckCircle className="w-5 h-5 text-emerald-600" /><span className="text-sm text-slate-700">Enabled {data.count} whitelisted IPs</span></>
          ) : (
            <><XCircle className="w-5 h-5 text-slate-400" /><span className="text-sm text-slate-500">Disabled</span></>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
